import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, MenuItem, Paper, Skeleton, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography, alpha, useTheme,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { settingsService, ResultSettings } from '../../services/settingsService';
import { Grade } from '../../types';
import ConfirmationDialog from '../common/ConfirmationDialog';

/** Every grade the school has — a settings list is never paged. */
const ALL = 500;
const NO_PASS_FAIL = 'none';

/** One rule: what it is, the current choice spelled out, the control, and an example. */
const RuleCard: React.FC<{
  title: string;
  description: string;
  example?: React.ReactNode;
  control: React.ReactNode;
  muted?: boolean;
}> = ({ title, description, example, control, muted }) => {
  const theme = useTheme();
  return (
    <Paper variant="outlined" sx={{ borderRadius: '12px', p: 2, opacity: muted ? 0.6 : 1, transition: 'opacity 150ms' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
          <Typography variant="body2" color="text.secondary">{description}</Typography>
        </Box>
        <Box sx={{ flexShrink: 0 }}>{control}</Box>
      </Stack>
      {example && (
        <Box sx={{ mt: 1.5, px: 1.5, py: 1, borderRadius: '8px', bgcolor: alpha(theme.palette.text.primary, 0.04) }}>
          <Typography variant="caption" color="text.secondary">{example}</Typography>
        </Box>
      )}
    </Paper>
  );
};

const toggleSx = { '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: 1.75 } } as const;

interface PendingChange { patch: Partial<ResultSettings>; title: string; message: string; confirmText: string }

const RESCORED = 'Every saved result will be re-scored, so all screens, reports and transcripts follow the new rule.';

/** What confirming a rule change will do, in the admin's terms. */
const describeChange = (patch: Partial<ResultSettings>, rules: ResultSettings, grades: Grade[]): PendingChange => {
  if (patch.absentCountsAsZero !== undefined) {
    return patch.absentCountsAsZero
      ? {
        patch, confirmText: 'Count as zero', title: 'Count absent papers as zero?',
        message: `${RESCORED} Students with absent papers get lower exam percentages and grades, and fail those papers when pass and fail are on.`,
      }
      : {
        patch, confirmText: 'Leave out', title: 'Leave absent papers out?',
        message: `${RESCORED} Absent papers stop counting, so exam percentages and grades rise for students who missed papers.`,
      };
  }
  if (patch.failingGradeId !== undefined) {
    const grade = grades.find((g) => g._id === patch.failingGradeId);
    if (!grade) {
      return {
        patch, confirmText: 'Switch off', title: 'Switch off pass and fail?',
        message: 'Pass and fail will be removed from every exam result, report and transcript. Grades stay as they are.',
      };
    }
    return {
      patch, confirmText: `Use ${grade.name}`, title: `Make ${grade.name} the failing grade?`,
      message: rules.failingGradeId
        ? `${RESCORED} A paper graded ${grade.name} or lower is failed, so some students may move between pass and fail.`
        : `Pass and fail will show on every exam result, report and transcript. A paper graded ${grade.name} or lower is failed.`,
    };
  }
  return patch.passRule === 'every_subject'
    ? {
      patch, confirmText: 'Require every subject', title: 'Require every subject to pass?',
      message: 'Students who failed any paper will be marked Fail, even when their overall % passes.',
    }
    : {
      patch, confirmText: 'Use overall %', title: 'Decide by the overall % only?',
      message: 'Students with a failed paper will be marked Pass when their overall % passes.',
    };
};

/**
 * Settings → Results: how a student's exam result is decided. Each change is
 * saved straight away and the server re-scores every saved result, so every
 * screen, report and transcript follows the new rule at once.
 */
const ResultsSettings: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { canWrite } = useModulePermissions();
  const writable = canWrite('settings_results');
  const [rules, setRules] = useState<ResultSettings | null>(null);
  const [grades, setGrades] = useState<Grade[]>([]);
  const [saving, setSaving] = useState(false);
  /** A change waiting for the admin to confirm it. Controls keep showing the saved rule until then. */
  const [pending, setPending] = useState<PendingChange | null>(null);

  useEffect(() => {
    Promise.all([settingsService.getResultSettings(), settingsService.getGrades({ isActive: true, limit: ALL })])
      .then(([r, g]) => {
        setRules(r.data ?? null);
        setGrades(g.data?.grades ?? []);
      })
      .catch((e: any) => enqueueSnackbar(e?.response?.data?.message || 'Could not load the result settings', { variant: 'error' }));
  }, [enqueueSnackbar]);

  const confirm = useCallback(async () => {
    if (!pending) return;
    setSaving(true);
    try {
      const res = await settingsService.updateResultSettings(pending.patch);
      const n = res.data?.updatedResults ?? 0;
      setRules(res.data?.settings ?? null);
      setPending(null);
      enqueueSnackbar(n ? `Saved — ${n} result${n === 1 ? '' : 's'} updated.` : 'Saved — no saved results needed updating.', { variant: 'success' });
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Could not save the result settings', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }, [pending, enqueueSnackbar]);

  const sortedGrades = useMemo(() => [...grades].sort((a, b) => b.minPercentage - a.minPercentage), [grades]);
  const failing = grades.find((g) => g._id === rules?.failingGradeId) ?? null;

  if (!rules) {
    return (
      <Stack spacing={2}>
        {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={96} sx={{ borderRadius: '12px' }} />)}
      </Stack>
    );
  }

  const disabled = !writable || saving;
  const request = (patch: Partial<ResultSettings>) => setPending(describeChange(patch, rules, grades));
  // The lowest grade above the failing one: what a pass needs.
  const passMark = failing ? [...sortedGrades].reverse().find((g) => g.minPercentage > failing.minPercentage) ?? null : null;

  return (
    <Stack spacing={2}>
      <RuleCard
        title="Absent papers"
        description={rules.absentCountsAsZero
          ? 'An absent paper counts as zero: its marks stay in the total, so absence lowers the exam % and fails that subject.'
          : "An absent paper isn't counted: the exam % is over the papers the student sat."}
        example={rules.absentCountsAsZero
          ? 'Absent in Urdu (out of 100), 450/500 in the rest → 450/600 = 75%'
          : 'Absent in Urdu (out of 100), 450/500 in the rest → 450/500 = 90%'}
        control={(
          <ToggleButtonGroup exclusive size="small" value={rules.absentCountsAsZero ? 'zero' : 'exclude'} disabled={disabled}
            onChange={(_, v) => v && request({ absentCountsAsZero: v === 'zero' })} sx={toggleSx}>
            <ToggleButton value="exclude">Leave out</ToggleButton>
            <ToggleButton value="zero">Count as zero</ToggleButton>
          </ToggleButtonGroup>
        )}
      />

      <RuleCard
        title="Failing grade"
        description={failing
          ? `A paper graded ${failing.name} (${failing.minPercentage}% – ${failing.maxPercentage}%) or lower is failed.`
          : 'Results show grades only, with no pass or fail. Pick a grade to switch pass and fail on.'}
        example={failing && passMark ? `Passing a paper needs ${passMark.minPercentage}% or more (grade ${passMark.name}).` : undefined}
        control={(
          <TextField
            select size="small" label="Failing grade" sx={{ minWidth: 220 }} disabled={disabled || grades.length === 0}
            value={rules.failingGradeId ?? NO_PASS_FAIL}
            onChange={(e) => request({ failingGradeId: e.target.value === NO_PASS_FAIL ? null : e.target.value })}
            helperText={grades.length === 0 ? 'Add grades first' : undefined}
          >
            <MenuItem value={NO_PASS_FAIL}><em>No pass or fail</em></MenuItem>
            {sortedGrades.map((g) => (
              <MenuItem key={g._id} value={g._id}>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: g.color, mr: 1.25, flexShrink: 0 }} />
                  {g.name}
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {g.minPercentage}% – {g.maxPercentage}%
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>
        )}
      />

      <RuleCard
        muted={!failing}
        title="Overall result"
        description={rules.passRule === 'every_subject'
          ? 'The exam is passed only when every paper is passed.'
          : 'The exam is passed when the overall % passes, even with a failed paper.'}
        example={failing
          ? `88% overall with Chemistry failed → ${rules.passRule === 'every_subject' ? 'Fail' : 'Pass'}`
          : 'Applies once a failing grade is picked.'}
        control={(
          <ToggleButtonGroup exclusive size="small" value={rules.passRule} disabled={disabled || !failing}
            onChange={(_, v) => v && request({ passRule: v })} sx={toggleSx}>
            <ToggleButton value="overall">Overall % only</ToggleButton>
            <ToggleButton value="every_subject">Every subject must pass</ToggleButton>
          </ToggleButtonGroup>
        )}
      />

      <ConfirmationDialog
        open={!!pending}
        onClose={() => !saving && setPending(null)}
        onConfirm={confirm}
        title={pending?.title ?? ''}
        message={pending?.message ?? ''}
        confirmText={pending?.confirmText}
        confirmColor="primary"
        loading={saving}
      />
    </Stack>
  );
};

export default ResultsSettings;
